/**
 * Página post-receta — Agendar próxima cita.
 *
 * Se accede tras crear (u omitir) la receta. Fecha por defecto: hoy + 14 días
 * (dos semanas) como mínimo según requisito del negocio. El doctor ajusta
 * fecha/hora, duración y motivo, o "Omitir" para volver al paciente.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatErr } from "../../lib/api";
import { StepIndicator } from "./PostConsultReceta";
import { ArrowLeft, Calendar, Clock } from "@/components/icons";

/** Formatea un Date a string "YYYY-MM-DDTHH:MM" para <input type="datetime-local"> */
function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Devuelve un Date con la fecha objetivo (hoy + 14 días) a las 10:00. */
function defaultNextDate() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  d.setHours(10, 0, 0, 0);
  return d;
}

export default function PostConsultNextAppt() {
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const patientId = location.state?.patientId;
  const patientName = location.state?.patientName || "Paciente";
  const followUpText = location.state?.followUp || "";

  // Mínimo 14 días desde hoy — la fecha inicial y la fecha mínima del picker.
  const minDate = useMemo(() => defaultNextDate(), []);
  const minDateStr = toLocalInput(minDate);
  const [startsAt, setStartsAt] = useState(minDateStr);
  const [durationMin, setDurationMin] = useState(30);
  const [reason, setReason] = useState(followUpText || "Control");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patientId) {
      toast.error("Falta información del paciente.");
      navigate("/doctor/agenda", { replace: true });
    }
  }, [patientId, navigate]);

  const skip = () => {
    navigate(`/doctor/pacientes/${patientId}`, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validar que la fecha elegida sea >= min (14 días).
    if (new Date(startsAt) < minDate) {
      toast.error("La próxima cita debe ser en al menos 2 semanas.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: patientId,
        date: new Date(startsAt).toISOString(),
        duration_min: Number(durationMin) || 30,
        reason: reason.trim() || "Control",
        status: "scheduled",
      });
      toast.success("Próxima cita agendada");
      navigate(`/doctor/pacientes/${patientId}`, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up max-w-3xl mx-auto space-y-4" data-testid="post-next-page">
      <div className="flex items-center justify-between">
        <Link
          to={`/doctor/pacientes/${patientId || ""}`}
          className="inline-flex items-center gap-2 text-black/60 font-semibold text-[13px] hover:text-black transition"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          Ir al paciente
        </Link>
        <StepIndicator current={2} />
      </div>

      <form
        onSubmit={submit}
        className="bg-[#F5F5F7] rounded-[28px] p-6 sm:p-8 space-y-5"
        data-testid="next-appt-form"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#0071E3] flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" strokeWidth={1.75} weight="regular" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
              Próxima cita
            </p>
            <h2 className="text-[22px] font-semibold text-black leading-tight tracking-tight">
              Agendar control con {patientName}
            </h2>
            <p className="text-[13px] text-black/55 mt-0.5">
              Debe ser en al menos 2 semanas desde hoy.
            </p>
          </div>
        </div>

        {followUpText && (
          <div className="bg-white rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#0071E3] mb-1">
              Sugerido por Ema
            </p>
            <p className="text-[13px] text-black">{followUpText}</p>
          </div>
        )}

        <div className="bg-white rounded-[20px] p-4 space-y-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
              Fecha y hora
            </span>
            <div className="relative">
              <input
                type="datetime-local"
                value={startsAt}
                min={minDateStr}
                onChange={(e) => setStartsAt(e.target.value)}
                required
                data-testid="next-date-input"
                className="w-full bg-white rounded-xl border border-[#D1D1D6] pl-10 pr-3 h-10 text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
              />
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" strokeWidth={1.75} weight="regular" />
            </div>
            <p className="text-[11px] text-black/50 mt-1">
              Mínimo: {minDate.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Duración (minutos)
              </span>
              <input
                type="number"
                min="10"
                max="240"
                step="5"
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                data-testid="next-duration-input"
                className="w-full bg-white rounded-xl border border-[#D1D1D6] px-3 h-10 text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                Motivo
              </span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Control, seguimiento, revisión de resultados…"
                data-testid="next-reason-input"
                className="w-full bg-white rounded-xl border border-[#D1D1D6] px-3 h-10 text-[13px] text-black placeholder:text-black/35 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
              />
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={skip}
            data-testid="next-skip"
            className="inline-flex items-center justify-center bg-white hover:bg-black/[0.03] text-black rounded-full px-5 h-11 font-semibold text-[13px] transition"
          >
            Omitir por ahora
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="next-confirm"
            className="inline-flex items-center justify-center gap-2 bg-[#0071E3] text-white hover:bg-[#0077ED] rounded-full px-5 h-11 font-semibold text-[13px] transition disabled:opacity-40"
          >
            {saving ? "Agendando…" : "Agendar próxima cita"}
          </button>
        </div>
      </form>
    </div>
  );
}
